import React from 'react';
import { ConfigProvider, theme } from 'antd';
import { useTheme } from '../contexts/ThemeContext';

interface ThemeConfigProviderProps {
  children: React.ReactNode;
}

const ThemeConfigProvider: React.FC<ThemeConfigProviderProps> = ({ children }) => {
  const { resolvedTheme } = useTheme();

  // Ant Design theme configuration using our CSS custom properties
  const antTheme = {
    algorithm: resolvedTheme === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      // Primary colors
      colorPrimary: 'rgb(var(--accent-primary))',
      colorPrimaryHover: 'rgb(var(--accent-primary-hover))',
      colorPrimaryActive: 'rgb(var(--accent-primary-hover))',

      // Background colors
      colorBgContainer: 'rgb(var(--card-bg))',
      colorBgElevated: 'rgb(var(--card-bg))',
      colorBgLayout: 'rgb(var(--bg-primary))',

      // Text colors
      colorText: 'rgb(var(--text-primary))',
      colorTextSecondary: 'rgb(var(--text-secondary))',
      colorTextTertiary: 'rgb(var(--text-muted))',
      colorTextQuaternary: 'rgb(var(--text-muted))',

      // Border colors
      colorBorder: 'rgb(var(--border-color))',
      colorBorderSecondary: 'rgb(var(--border-color))',

      // Component specific colors
      colorBgBase: 'rgb(var(--bg-primary))',

      // Table specific
      colorBgContainerDisabled: 'rgb(var(--bg-secondary))',
      colorFillAlter: 'rgb(var(--bg-secondary))',
      colorFillContent: 'rgb(var(--bg-secondary))',

      // Status colors using our theme variables
      colorSuccess: 'rgb(var(--accent-success))',
      colorWarning: 'rgb(var(--accent-warning))',
      colorError: 'rgb(var(--accent-error))',
      colorInfo: 'rgb(var(--accent-secondary))',

      // Link colors
      colorLink: 'rgb(var(--accent-primary))',
      colorLinkHover: 'rgb(var(--accent-primary-hover))',
      colorLinkActive: 'rgb(var(--accent-primary-hover))',
    },
    components: {
      Card: {
        colorBgContainer: 'rgb(var(--card-bg))',
        colorBorderSecondary: 'rgb(var(--card-border))',
        colorText: 'rgb(var(--text-primary))',
        colorTextSecondary: 'rgb(var(--text-secondary))',
      },
      Table: {
        colorBgContainer: 'rgb(var(--card-bg))',
        colorText: 'rgb(var(--text-primary))',
        colorTextSecondary: 'rgb(var(--text-secondary))',
        colorBorderSecondary: 'rgb(var(--border-color))',
        colorFillAlter: 'rgb(var(--bg-secondary))',
      },
      Tabs: {
        colorBgContainer: 'rgb(var(--bg-primary))',
        colorText: 'rgb(var(--text-primary))',
        colorTextSecondary: 'rgb(var(--text-secondary))',
        colorBorderSecondary: 'rgb(var(--border-color))',
        colorPrimary: 'rgb(var(--accent-primary))',
      },
      Modal: {
        colorBgElevated: 'rgb(var(--card-bg))',
        colorBgMask: resolvedTheme === 'dark' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.45)',
        colorText: 'rgb(var(--text-primary))',
        colorTextSecondary: 'rgb(var(--text-secondary))',
      },
      Button: {
        colorBgContainer: 'rgb(var(--card-bg))',
        colorText: 'rgb(var(--text-primary))',
        colorBorder: 'rgb(var(--border-color))',
        colorPrimary: 'rgb(var(--accent-primary))',
        colorPrimaryHover: 'rgb(var(--accent-primary-hover))',
        colorPrimaryActive: 'rgb(var(--accent-primary-hover))',
      },
      Input: {
        colorBgContainer: 'rgb(var(--card-bg))',
        colorBorder: 'rgb(var(--border-color))',
        colorText: 'rgb(var(--text-primary))',
        colorTextPlaceholder: 'rgb(var(--text-muted))',
      },
      Select: {
        colorBgContainer: 'rgb(var(--card-bg))',
        colorBorder: 'rgb(var(--border-color))',
        colorText: 'rgb(var(--text-primary))',
        colorBgElevated: 'rgb(var(--card-bg))',
      },
      Divider: {
        colorSplit: 'rgb(var(--border-color))',
      },
    },
  };

  return (
    <ConfigProvider theme={antTheme}>
      {children}
    </ConfigProvider>
  );
};

export default ThemeConfigProvider;
